/* eslint-disable react/forbid-prop-types */
import React, { useState } from 'react'
import PropTypes from 'prop-types'
import i18n from 'simple-react-i18n'
import { Dialog, DialogActions, DialogContent, DialogTitle, Grid, Step, StepLabel, Stepper } from '@mui/material'
import { makeStyles } from '@mui/styles'
import { hasBooleanValue, hasValue } from 'utils/NumberUtil'
import useUpdateEffect from 'utils/customHook/useUpdateEffect'
import { ButtonMUI } from 'components/styled/Buttons'
import { DefaultDialogTitle } from 'components/styled/Dialog'
import { uniq } from 'lodash'

const useStyles = makeStyles(() => ({
    root: {
        fontWeight: 'bold',
        '&.Mui-expanded': {
            minHeight: 45,
            maxHeight: 45,
        },
    },
    label: {
        '&.MuiStepLabel-alternativeLabel': {
            marginTop: '3px !important',
            fontSize: '8px',
        },
    },
}))

const StepperDialog = ({
    steps = [],
    open = false,
    defaultStep = 0,
    title = '',
    closeDialog = () => {},
    children = () => {},
    contentStyle = {},
    renderDeleteButton = () => {},
    renderSaveButton = () => {},
    sx = {},
    maxWidth = 'lg',
    moreSaveButtons = false,
    bypassValidateSteps = false,
}) => {
    const classes = useStyles()

    const [step, setStep] = useState(defaultStep)
    const [validateSteps, setValidateSteps] = useState(bypassValidateSteps ? steps?.map(s => s.constant) : [])

    const isLastStep = () => step === steps[steps.length-1]?.constant

    const nextStep = () => {
        if (!bypassValidateSteps) {
            setValidateSteps(uniq([ ...validateSteps, step ]))
        }

        setStep(s => steps.find(st => st.constant === s)?.nextStep ?? s + 1)
    }

    const prevStep = () => setStep(s => steps.find(st => st.constant === s)?.previousStep ?? s - 1)

    const resetStep = () => {
        if (!bypassValidateSteps) {
            setValidateSteps([])
        }

        setStep(0)
    }

    useUpdateEffect(resetStep, [open])

    const filteredSteps = steps.filter(s => !s.hidden)
    const activeStep = filteredSteps.findIndex(s => s.constant === step)

    return (
        <Dialog
            fullWidth
            maxWidth={maxWidth}
            open={open}
            slotProps={{
                paper: {
                    sx: {
                        minHeight: '90vh',
                        maxHeight: '90vh',
                        overflowY: 'hidden',
                        ...sx,
                    },
                },
            }}
        >
            <DefaultDialogTitle title={title} onClose={() => closeDialog(resetStep)} />
            <DialogTitle sx={{ margin: '0', padding: '0' }}>
                <Grid container justifyContent='center' style={{ backgroundColor: 'white', paddingTop: '5px' }}>
                    <Grid size={11}>
                        <Stepper
                            alternativeLabel
                            activeStep={activeStep}
                            style={{ zoom: '2' }}
                        >
                            {filteredSteps.map((s, idx) => (
                                <Step
                                    key={idx}
                                    className='clickable'
                                    onClick={() => {
                                        if (!validateSteps.includes(s.constant)) return
                                        if (steps[s.constant]?.onChangeStep) steps[s.constant]?.onChangeStep()
                                        setStep(s.constant)
                                    }}
                                >
                                    <StepLabel classes={classes}>{s.label}</StepLabel>
                                </Step>
                            ))}
                        </Stepper>
                    </Grid>
                </Grid>
            </DialogTitle>
            <DialogContent style={contentStyle}>
                {children(step, nextStep, prevStep, resetStep)}
            </DialogContent>
            <DialogActions sx={{ display: 'block' }}>
                <Grid container justifyContent='space-between'>
                    <Grid container size={moreSaveButtons ? 4 : 3} justifyContent='flex-start'>
                        {renderDeleteButton(step)}
                    </Grid>
                    <Grid container size={moreSaveButtons ? 2 : 3} justifyContent='center'>
                        <ButtonMUI
                            onClick={() => {
                                if (hasValue(steps[step].onPrev)) {
                                    steps[step].onPrev(prevStep)
                                } else {
                                    prevStep()
                                }
                            }}
                            disabled={step === 0 || steps[step].canGoBack === false}
                            variant='outlined'
                            color='primary'
                        >
                            {i18n.previous}
                        </ButtonMUI>
                    </Grid>
                    <Grid container size={moreSaveButtons ? 2 : 3} justifyContent='center'>
                        <ButtonMUI
                            onClick={() => {
                                if (hasValue(steps[step].onNext)) {
                                    steps[step].onNext(nextStep)
                                } else {
                                    nextStep()
                                }
                            }}
                            disabled={hasBooleanValue(steps[step].nextAvailable) ? !steps[step].nextAvailable : isLastStep()}
                            variant='outlined'
                            color='primary'
                        >
                            {steps[step].nextLabel || i18n.next}
                        </ButtonMUI>
                    </Grid>
                    <Grid container size={moreSaveButtons ? 4 : 3} justifyContent='flex-end'>
                        {renderSaveButton(step, resetStep)}
                    </Grid>
                </Grid>
            </DialogActions>
        </Dialog>
    )
}

StepperDialog.propTypes = {
    steps: PropTypes.arrayOf(PropTypes.shape({
        label: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
        constant: PropTypes.number,
        nextAvailable: PropTypes.bool,
        onNext: PropTypes.func,
        nextStep: PropTypes.number,
        previousStep: PropTypes.number,
        onPrev: PropTypes.func,
        onChangeStep: PropTypes.func,
    })).isRequired,
    closeDialog: PropTypes.func.isRequired,
    open: PropTypes.bool.isRequired,
    defaultStep: PropTypes.number,
    title: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
    children: PropTypes.func.isRequired,
    contentStyle: PropTypes.object,
    renderDeleteButton: PropTypes.func,
    renderSaveButton: PropTypes.func,
    sx: PropTypes.object,
    maxWidth: PropTypes.string,
    moreSaveButtons: PropTypes.bool,
    bypassValidateSteps: PropTypes.bool,
}

export default StepperDialog
